import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { api, extractError } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { FieldTip } from '@/components/FieldTip.js';
import { useWorkspace } from '@/context/WorkspaceContext.js';
import { EvolutionChart, type EvolutionPoint } from '@/features/cinco-forcas/EvolutionChart.js';
import { NAV_LABELS } from '@/lib/nav-labels.js';
import { GerirEmpresasModal } from '@/components/GerirEmpresasModal.js';
import { WorkspaceSwitcher } from '@/components/WorkspaceSwitcher.js';

export function MetodoDiagnosticoPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeEmpresaId, activeEmpresa, setActiveEmpresaId, isLoading: wsLoading } = useWorkspace();
  const [newDiagTitle, setNewDiagTitle] = useState('');
  const [gerirOpen, setGerirOpen] = useState(false);

  const q = useQuery({
    queryKey: ['empresa', activeEmpresaId],
    queryFn: async () =>
      (await api.get(`/empresas/${activeEmpresaId}`)).data as {
        name: string;
        segment: string | null;
        meu_papel: string;
        diagnosticos: {
          id: string;
          title: string | null;
          status: string;
          respostas_count: number;
        }[];
        evolution: EvolutionPoint[];
      },
    enabled: !!activeEmpresaId,
  });

  const arquivarDiag = useMutation({
    mutationFn: async (diagId: string) => (await api.post(`/diagnosticos/${diagId}/arquivar`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empresa', activeEmpresaId] }),
  });

  if (wsLoading) {
    return <PageLayout title={NAV_LABELS.diagnostico5Forcas}>Carregando…</PageLayout>;
  }

  if (!activeEmpresaId) {
    return (
      <PageLayout title={NAV_LABELS.diagnostico5Forcas}>
        <WorkspaceSwitcher variant="page" />
        <p className="mb-4 text-ha-subtle">Adicione uma empresa para começar o diagnóstico.</p>
        <button
          type="button"
          className="rounded-element px-4 py-2 text-xs font-bold uppercase text-black"
          style={{ background: '#ffc800' }}
          onClick={() => setGerirOpen(true)}
        >
          Gerir empresas
        </button>
        <GerirEmpresasModal open={gerirOpen} onOpenChange={setGerirOpen} />
      </PageLayout>
    );
  }

  if (q.isLoading) {
    return <PageLayout title={NAV_LABELS.diagnostico5Forcas}>Carregando…</PageLayout>;
  }

  if (q.isError || !q.data) {
    const err = q.error ? extractError(q.error) : null;
    const isNotFound =
      err?.code === 'NOT_FOUND' ||
      (q.error &&
        typeof q.error === 'object' &&
        'isAxiosError' in q.error &&
        (q.error as { response?: { status?: number } }).response?.status === 404);
    return (
      <PageLayout title={NAV_LABELS.diagnostico5Forcas}>
        <WorkspaceSwitcher variant="page" />
        <p className="mb-4 text-sm text-feedback">
          {err?.message ?? 'Não foi possível carregar a empresa ativa.'}
        </p>
        {isNotFound && (
          <p className="mb-4 text-sm text-ha-subtle">
            A empresa guardada já não existe (pode ter sido recriada após manutenção). Escolha outra ou
            crie uma nova.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-element px-4 py-2 text-xs font-bold uppercase text-black"
            style={{ background: '#ffc800' }}
            onClick={() => {
              if (isNotFound) setActiveEmpresaId(null);
              setGerirOpen(true);
            }}
          >
            Gerir empresas
          </button>
          {isNotFound && (
            <button
              type="button"
              className="rounded-element border border-ha-border2 px-4 py-2 text-xs font-bold uppercase text-ha-subtle"
              onClick={() => setActiveEmpresaId(null)}
            >
              Limpar seleção
            </button>
          )}
          <button
            type="button"
            className="rounded-element border border-ha-border2 px-4 py-2 text-xs text-ha-subtle"
            onClick={() => void q.refetch()}
          >
            Tentar novamente
          </button>
        </div>
        <GerirEmpresasModal open={gerirOpen} onOpenChange={setGerirOpen} />
      </PageLayout>
    );
  }

  const data = q.data;
  const isDono = data.meu_papel === 'DONO';

  return (
    <PageLayout
      title={NAV_LABELS.diagnostico5Forcas}
      description={`Empresa ativa: ${activeEmpresa?.name ?? data.name}`}
      actions={
        <button
          type="button"
          className="rounded-element border border-ha-border2 px-3 py-1.5 text-[10px] font-bold uppercase text-ha-subtle hover:border-accent"
          onClick={() => setGerirOpen(true)}
        >
          Gerir empresas
        </button>
      }
    >
      <WorkspaceSwitcher variant="page" />
      {data.segment && <p className="mb-4 text-sm text-ha-muted">Segmento: {data.segment}</p>}

      <EvolutionChart points={data.evolution} />

      {isDono && (
        <div className="mb-6 flex max-w-xl flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <FieldTip
              label="Título do novo diagnóstico (opcional)"
              hint="Ex.: Q1 2026 — aparece no histórico."
            >
              <input
                className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2 text-sm"
                placeholder="Opcional"
                value={newDiagTitle}
                onChange={(e) => setNewDiagTitle(e.target.value)}
              />
            </FieldTip>
          </div>
          <button
            type="button"
            className="px-4 py-2 text-xs font-bold uppercase text-black"
            style={{ background: '#ffc800' }}
            onClick={() => {
              const t = newDiagTitle.trim();
              const qs = t ? `?t=${encodeURIComponent(t)}` : '';
              void nav(`/empresa/${activeEmpresaId}/diagnostico/novo${qs}`);
            }}
          >
            Novo diagnóstico
          </button>
        </div>
      )}

      <h2 className="mb-2 text-sm font-bold uppercase text-ha-muted">Histórico</h2>
      <ul className="space-y-2">
        {data.diagnosticos.length === 0 && (
          <li className="text-sm text-ha-subtle">Nenhum diagnóstico ainda.</li>
        )}
        {data.diagnosticos.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="text-left text-accent underline"
              onClick={() => nav(`/diagnostico/${d.id}`)}
            >
              {d.title || 'Diagnóstico'} — {d.respostas_count} respostas — {d.status}
            </button>
            {isDono && d.status === 'ATIVO' && (
              <AlertDialog.Root>
                <AlertDialog.Trigger asChild>
                  <button type="button" className="text-xs text-ha-muted underline">
                    Arquivar
                  </button>
                </AlertDialog.Trigger>
                <AlertDialog.Portal>
                  <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
                  <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(90vw,24rem)] -translate-x-1/2 -translate-y-1/2 border border-ha-border bg-ha-card p-4">
                    <AlertDialog.Title className="text-sm font-bold">Arquivar diagnóstico?</AlertDialog.Title>
                    <AlertDialog.Description className="mt-2 text-xs text-ha-subtle">
                      Deixa de aceitar novas respostas.
                    </AlertDialog.Description>
                    <div className="mt-4 flex justify-end gap-2">
                      <AlertDialog.Cancel asChild>
                        <button type="button" className="px-3 py-1 text-xs">
                          Cancelar
                        </button>
                      </AlertDialog.Cancel>
                      <AlertDialog.Action asChild>
                        <button
                          type="button"
                          className="bg-accent px-3 py-1 text-xs font-bold text-black"
                          onClick={() => arquivarDiag.mutate(d.id)}
                        >
                          Arquivar
                        </button>
                      </AlertDialog.Action>
                    </div>
                  </AlertDialog.Content>
                </AlertDialog.Portal>
              </AlertDialog.Root>
            )}
          </li>
        ))}
      </ul>

      <GerirEmpresasModal open={gerirOpen} onOpenChange={setGerirOpen} />
    </PageLayout>
  );
}
